/**
 * Quick create, hosted in the shell drawer — the Elevate-sidepane pattern doing
 * the one job a band cannot do for itself.
 *
 * A related list inside a grid row is horizontally squeezed; a create form is
 * the thing that needs room. So the invoker only says WHAT to create (a
 * `QuickCreateRequest`) and the drawer supplies the space, the width and the
 * survival — the page underneath can scroll, switch tabs or drill deeper and
 * the draft stays put.
 *
 * ── The three controls in the header, and why they are where they are ──────
 *  ⌘   LEFT of the title, where every other ⌘ button in the console sits
 *      (`RecordRelations`, the pipeline subgrids). It opens the palette scoped
 *      to THIS draft — and, because a create is not a list, promotes to the
 *      full form on the way, so the commands and the fields they act on are
 *      both on screen. ⌘K on the keyboard does exactly the same thing, which is
 *      the point: the button is a discoverable shadow of the shortcut, not a
 *      second mechanism.
 *  ⤢   Promote to the full form WITHOUT opening the palette — for the reader
 *      who wants more fields and no command list.
 *  ✕   Close. The draft dies with it; nothing is written until Create.
 *
 * ── What it writes ─────────────────────────────────────────────────────────
 * `core.mutate_relationship` — the registered create verb owns the row and link
 * in one transaction. The host never chooses a typed door or attempts two
 * client writes that could leave an unreachable record behind.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Row } from '@solum/sdk'
import { Form, MarbleMark, type FormField } from '@solum/ui'
import { openPalette, type PaletteScope } from '../app/paletteScope'
import { displayLabel, useSchema, useSemantics } from '../data/queries'
import { useSolum } from '../data/client'
import { refTarget } from '../form/RefPicker'
import { advancedLookupFields, makeEntitySearch } from '../form/entityLookup'
import {
  fullCreateFields, quickCreateFields, relationshipAttributeFields, useCreateRelated, useLinkRelation,
  useRelationSearch, useRelationTargetFields,
} from './useRelations'
import { decideRelatedCreate, personIdentityFingerprint, type PersonCreateReview } from './personCreateGuard'
import {
  closeQuickCreate, expandQuickCreate, seedQuickCreate, useQuickCreate,
  type QuickCreateRequest,
} from './quickCreate'
import s from './QuickCreatePanel.module.css'

/** Namespaces edge facts so an endpoint column with the same key cannot collide. */
export const EDGE_DRAFT_PREFIX = '__edge__:'

export function splitRelatedDraft(draft: Record<string, unknown>): { record: Row; edgeAttributes: Row } {
  const record: Row = {}
  const edgeAttributes: Row = {}
  for (const [key, value] of Object.entries(draft)) {
    if (key.startsWith(EDGE_DRAFT_PREFIX)) edgeAttributes[key.slice(EDGE_DRAFT_PREFIX.length)] = value
    else record[key] = value
  }
  return { record, edgeAttributes }
}

/** What the palette offers while a draft is open: the draft's own verbs. */
function draftScope(req: QuickCreateRequest, submit: () => void): PaletteScope {
  return {
    entity: req.slot.otherEntity,
    label: `New ${req.slot.otherLabel.toLowerCase()}`,
    verbs: [
      { label: `Create ${req.slot.otherLabel.toLowerCase()}`, run: submit },
      { label: 'Show every field', run: expandQuickCreate },
      { label: 'Discard this draft', run: closeQuickCreate },
    ],
  }
}

export function QuickCreatePanel() {
  const req = useQuickCreate()
  if (!req) {
    return (
      <div className={s.empty} data-testid="quick-create-empty">
        ＋
        <p>Add something to a record — the form docks here, with room to type.</p>
      </div>
    )
  }
  // Keyed on the slot + record so switching what you are creating starts a new
  // draft rather than inheriting the last one's values. The MODE is not in the
  // key: promoting quick → full must keep what has been typed.
  return <Draft key={`${req.slot.id}:${req.recordId}`} req={req} />
}

function Draft({ req }: { req: QuickCreateRequest }) {
  const { slot, recordId, mode } = req
  const fields = useRelationTargetFields(slot)
  const create = useCreateRelated(slot, recordId)
  const link = useLinkRelation(slot, recordId)
  const search = useRelationSearch(slot)
  const [draft, setDraft] = useState<Record<string, unknown>>(req.initial ?? {})
  const [review, setReview] = useState<PersonCreateReview | null>(null)
  const [guardError, setGuardError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  const schema = useSchema()
  const allSemantics = useSemantics()
  const { client } = useSolum()
  const queryClient = useQueryClient()
  const edgeFields = useMemo(() => relationshipAttributeFields(slot, EDGE_DRAFT_PREFIX), [slot])
  const shown = useMemo(
    () => [...wireRelations(
      mode === 'full' ? fullCreateFields(fields) : quickCreateFields(fields),
      slot.otherEntity, schema.data, client.raw(), client, queryClient,
      allSemantics.data?.fields ?? [], draft), ...edgeFields],
    [mode, fields, slot.otherEntity, schema.data, client, queryClient, allSemantics.data, draft, edgeFields])
  const hiddenCount = useMemo(
    () => Math.max(0, fullCreateFields(fields).length - quickCreateFields(fields).length),
    [fields])

  // Write the draft behind the store so a promotion re-reads it rather than
  // resetting to the initial seed.
  useEffect(() => { seedQuickCreate(draft) }, [draft])

  const submit = async (approvedFingerprint?: string) => {
    setGuardError(null)
    setChecking(true)
    try {
      const parts = splitRelatedDraft(draft)
      const decision = await decideRelatedCreate(slot.otherEntity, parts.record, search, approvedFingerprint)
      if (decision.kind === 'review') {
        setReview(decision)
        return undefined
      }
      const created = await create.mutateAsync(parts)
      closeQuickCreate()
      return created
    } catch (e) {
      setGuardError((e as Error)?.message || 'That person could not be checked against the directory.')
      return undefined
    } finally {
      setChecking(false)
    }
  }

  const chooseExisting = async (id: string) => {
    setGuardError(null)
    try {
      await link.mutateAsync({ otherId: id, edgeAttributes: splitRelatedDraft(draft).edgeAttributes })
      closeQuickCreate()
    } catch (e) {
      setGuardError((e as Error)?.message || 'That person could not be linked.')
    }
  }

  const promoteAndCommand = () => {
    expandQuickCreate()
    openPalette(draftScope({ ...req, mode: 'full' }, () => { void submit() }))
  }

  /**
   * ⌘K while a draft is open.
   *
   * Capture phase, and it stops propagation: the shell binds ⌘K on `window` in
   * the BUBBLE phase (`AppShell.tsx`), so without this both handlers run and the
   * palette opens unscoped, immediately after we scoped it. Here we consume the
   * key and open the palette ourselves, with the draft's scope, having promoted
   * to the full form first — which is the whole behaviour: the commands and
   * every field they act on, together.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        e.stopPropagation()
        promoteAndCommand()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  const thing = slot.otherLabel.toLowerCase()

  return (
    <div className={s.wrap} data-testid="quick-create" data-mode={mode}>
      <header className={s.head}>
        {/* LEFT, as everywhere else in the console. */}
        <button
          className={s.cmd}
          data-testid="quick-create-cmd"
          title="Commands for this draft (⌘K) — opens every field"
          aria-label="Commands for this draft"
          onClick={promoteAndCommand}
        ><MarbleMark size={14} /></button>
        <div className={s.titles}>
          <h3 className={s.title}>New {thing}</h3>
          <p className={s.sub}>
            {slot.label}
            {req.recordLabel ? <> · on <strong>{req.recordLabel}</strong></> : null}
          </p>
        </div>
        {mode === 'quick' && hiddenCount > 0 && (
          <button className={s.icon} data-testid="quick-create-expand"
            title={`Show every field (${hiddenCount} more)`} aria-label="Show every field"
            onClick={expandQuickCreate}>⤢</button>
        )}
        <button className={s.icon} data-testid="quick-create-close"
          title="Discard" aria-label="Discard this draft" onClick={closeQuickCreate}>✕</button>
      </header>

      {mode === 'quick' && hiddenCount > 0 && (
        <p className={s.note}>
          The {shown.length} fields this {thing} needs.{' '}
          <button className={s.inline} onClick={expandQuickCreate}>Show all {shown.length + hiddenCount}</button>
        </p>
      )}

      {(guardError || create.isError || link.isError) && (
        <p className={s.error} role="alert" data-testid="quick-create-error">
          {guardError || (create.error as Error)?.message || (link.error as Error)?.message || 'That did not save.'}
        </p>
      )}

      {review && (
        <section className={s.review} aria-labelledby="person-review-title" data-testid="person-create-review">
          <h4 id="person-review-title">Could this person already exist?</h4>
          <p>Choose the existing person to add this relationship. Create another only after checking the details.</p>
          <div className={s.candidates}>
            {review.candidates.map((candidate) => (
              <button key={candidate.id} type="button" className={s.candidate}
                onClick={() => { void chooseExisting(candidate.id) }}>
                <strong>{candidate.primary}</strong>
                {candidate.secondary != null && <span>{candidate.secondary}</span>}
              </button>
            ))}
          </div>
          <button type="button" className={s.createAnyway}
            onClick={() => { void submit(review.fingerprint) }}>
            None of these — create a different person
          </button>
        </section>
      )}

      <div ref={formRef} className={s.body}>
        <Form
          fields={shown}
          value={draft}
          onChange={(patch) => setDraft((d) => {
            const next = { ...d, ...patch }
            if (review && personIdentityFingerprint(next) !== review.fingerprint) setReview(null)
            return next
          })}
          onSubmit={() => { void submit() }}
          onCancel={closeQuickCreate}
          submitLabel={`Create ${thing}`}
          variant={mode === 'quick' ? 'quick' : 'full'}
          layout={mode === 'quick' ? 'stack' : 'grid'}
          busy={create.isPending || link.isPending || checking}
          testid="quick-create-form"
        />
      </div>
    </div>
  )
}

/** The label a caller passes as `recordLabel`, from a row it already has. */
export const parentLabel = (row: Row | undefined): string | undefined =>
  row ? displayLabel(row) : undefined

/**
 * Turn a `relation` field (a `ref` column) into a real picker by attaching the
 * search over its target entity — the target derived from the metamodel exactly
 * as `RefPicker` does (a `storage='fk'` relationship whose column is this one).
 * A ref the metamodel does not describe stays a disabled relation field. It must
 * not become a raw-id input: missing and caller-hidden targets have the same
 * non-enumerating presentation.
 */
export function wireRelations(
  formFields: FormField[], entity: string,
  schema: Parameters<typeof refTarget>[0], raw: Parameters<typeof makeEntitySearch>[0],
  semanticClient: Parameters<typeof makeEntitySearch>[1], cache: Parameters<typeof makeEntitySearch>[4],
  semanticFields: Parameters<typeof advancedLookupFields>[0],
  draft: Record<string, unknown>,
): FormField[] {
  const resolutions = new Map(formFields.filter((field) => field.type === 'relation')
    .map((field) => [field.name, refTarget(schema, entity, field.name)] as const))
  const hidden = new Set([...resolutions.values()].flatMap((resolution) =>
    resolution.kind !== 'none' && resolution.discriminatorColumn ? [resolution.discriminatorColumn] : []))

  return formFields.filter((field) => !hidden.has(field.name)).map((f) => {
    if (f.type !== 'relation') return f
    const resolution = resolutions.get(f.name) ?? refTarget(schema, entity, f.name)
    if (resolution.kind === 'none') return {
      ...f,
      relation: { unavailableMessage: 'This relationship is unavailable.' },
    }
    const targets = resolution.kind === 'single' ? [resolution.target] : resolution.targets
    const discriminatorValue = resolution.discriminatorColumn && draft[resolution.discriminatorColumn] != null
      ? String(draft[resolution.discriminatorColumn])
      : null
    const active = resolution.kind === 'single'
      ? resolution.target
      : targets.find((target) => target.discriminatorValue === discriminatorValue)
    return {
      ...f,
      relation: {
        target: active?.entity,
        placeholder: active ? `Find ${active.label.toLowerCase()}…` : 'Choose a reference type…',
        discriminatorColumn: resolution.discriminatorColumn ?? undefined,
        defaultType: active?.discriminatorValue ?? undefined,
        cardinality: active?.cardinality,
        advanced: active
          ? { fields: advancedLookupFields(semanticFields.filter((field) => field.entity_type === active.entity)) }
          : undefined,
        types: resolution.kind === 'polymorphic'
          ? targets.map((target) => ({ key: target.discriminatorValue!, label: target.label }))
          : undefined,
        search: (query, facet, selectedType, filters) => {
          const target = resolution.kind === 'single'
            ? resolution.target
            : targets.find((candidate) => candidate.discriminatorValue === selectedType)
          return target
            ? makeEntitySearch(raw, semanticClient, schema, target.entity, cache)(query, facet, selectedType, filters)
            : Promise.resolve([])
        },
      },
    }
  })
}
